import { useState, useEffect } from 'react';
import { costsService } from '../../services/api';
import { formatMoney } from '../../utils/pricing';
import { COST_UNITS, todayBrisbane, fuelEstimate, FUEL_DEFAULT_L_PER_100KM } from '../../utils/costs';
import { formatApptDateLong } from '../../utils/dateFormatters';
import { useAuth } from '../../contexts/AuthContext';
import ReceiptThumbs from '../ReceiptThumbs';
import './JobCosts.css';

const fmtQty = (n) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/\.?0+$/, '');
};
const fmtDate = (ymd) => (ymd ? new Date(`${ymd}T12:00:00+10:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : '');
// "-$227.46" rather than "$-227.46" for a negative margin
const money = (v) => (Number(v) < 0 ? `-${formatMoney(Math.abs(Number(v)))}` : formatMoney(v));

/**
 * Internal costs of a job (backlog #675): cost lines by category with receipt
 * photos, and the job's P&L against what was quoted. Rendered inside the
 * appointment modal's form, so every control is type="button" and Enter is
 * caught here rather than submitting the appointment. `onLightbox` opens the
 * modal's lightbox (rendered outside the form) for receipt photos. `onChange`
 * gets the fresh costs after every change so the page around the panel (the
 * job view's P&L strip) keeps up without a reload (Bug #396).
 */
export default function JobCosts({ appointment = null, jobId = null, staff = [], onLightbox, onChange = null, hideSummary = false }) {
  const { user } = useAuth(); // a new cash line defaults its payer to whoever is logged in (backlog #685)
  const [data, setData] = useState(null); // { lines, summary, defaults, categories }
  const [form, setForm] = useState(null); // null | { id: null|number, ...fields }
  const [travel, setTravel] = useState(null); // null | { loading } | { home, destination, trips, km_one_way, l_per_100km, fuel_price, … }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Costs belong to the job. Opened from a visit without a known job id, the
  // appointment path resolves the job server-side and tags new lines with the day.
  const job = jobId || appointment?.job_id || null;
  async function load(changed = false) {
    const r = job ? await costsService.forJob(job) : await costsService.forAppointment(appointment.id);
    if (r.success) {
      const next = { lines: r.lines || [], summary: r.summary, defaults: r.defaults, categories: r.categories || [], jobNumber: r.job_number, jobId: r.job_id, visits: r.visits || [] };
      setData(next);
      if (changed) onChange?.(next);
    } else setError(r.error || 'Could not load costs');
  }
  useEffect(() => {
    setForm(null);
    setError('');
    if (job || appointment?.id) load();
  }, [job, appointment?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const cats = data?.categories || [];
  const crew = data?.defaults?.crew || [];
  // Who: every active person, falling back to the job's crew.
  const people = staff.length ? staff : crew;
  const catOf = (id) => cats.find((c) => String(c.id) === String(id));

  /** A fresh form for a category — its type decides what gets pre-filled. */
  function lineFor(cat, base = {}) {
    const d = data?.defaults || { hours: 1, crew: [], km_rate: 0 };
    const person = d.crew[0] || null;
    // Paid by (backlog #685): who fronted the cash — not labour (wages) or equipment (depreciation/hire), so those never carry it.
    // Starts as the usual payer from Settings (backlog #688), else whoever is logged in.
    const isCash = cat && cat.kind !== 'labour' && cat.kind !== 'equipment';
    const usualPayer = d.default_payer ? String(d.default_payer) : (user?.id ? String(user.id) : '');
    const f = {
      id: null, category_id: cat ? String(cat.id) : '', description: '', qty: '1', unit: cat?.unit || 'ea',
      unit_cost: '', incurred_on: todayBrisbane(), staff_id: '', vendor: '', includes_gst: true,
      appointment_id: appointment?.id ? String(appointment.id) : '', // the day, when opened from a visit
      equipment_id: '',
      paid_by: isCash ? usualPayer : ''
    };
    switch (cat?.kind) {
      case 'labour':
        f.qty = String(d.hours);
        f.unit = 'hr';
        f.staff_id = person ? String(person.id) : '';
        f.unit_cost = person?.hourly_cost != null ? String(person.hourly_cost) : '';
        f.description = person ? `Labour — ${person.name}` : 'Labour';
        break;
      case 'travel':
        f.qty = '';
        f.unit = 'km';
        f.unit_cost = d.km_rate ? String(d.km_rate) : '';
        f.description = 'Travel to site';
        break;
      case 'fuel':
        f.qty = '';
        f.unit = 'L';
        f.description = 'Fuel';
        break;
      case 'equipment': {
        // From the register on Business costs (GID-Session-015): the job's hours × the asset's hourly rate
        const kit = (d.equipment || [])[0] || null;
        f.qty = String(d.hours);
        f.unit = 'hr';
        if (kit) { f.equipment_id = String(kit.id); f.unit_cost = String(kit.hourly_rate); f.description = `${kit.name} — ${d.hours} h`; }
        break;
      }
      default:
        break;
    }
    return { ...f, ...base };
  }

  function startAdd() {
    setError('');
    setForm(lineFor(cats[0] || null));
  }
  function edit(line) {
    setError('');
    setForm({
      id: line.id, category_id: line.category_id ? String(line.category_id) : '', description: line.description,
      qty: fmtQty(line.qty), unit: line.unit || 'ea', unit_cost: String(line.unit_cost), incurred_on: line.incurred_on || todayBrisbane(),
      staff_id: line.staff_id ? String(line.staff_id) : '', vendor: line.vendor || '', includes_gst: !!line.includes_gst,
      appointment_id: line.appointment_id ? String(line.appointment_id) : '',
      equipment_id: line.equipment_id ? String(line.equipment_id) : '',
      paid_by: line.paid_by ? String(line.paid_by) : ''
    });
  }
  function changeCategory(id) {
    // Re-seed the defaults for the new type but keep what was typed on purpose.
    setForm((prev) => lineFor(catOf(id), { id: prev.id, incurred_on: prev.incurred_on, vendor: prev.vendor, appointment_id: prev.appointment_id }));
  }
  function changeEquipment(id) {
    const kit = (data?.defaults?.equipment || []).find((e) => String(e.id) === String(id));
    setForm((prev) => ({ ...prev, equipment_id: id, ...(kit ? { unit: 'hr', unit_cost: String(kit.hourly_rate), description: `${kit.name} — ${prev.qty || data?.defaults?.hours || 1} h` } : {}) }));
  }
  function changePerson(id) {
    const p = people.find((s) => String(s.id) === String(id));
    setForm((prev) => {
      const next = { ...prev, staff_id: id };
      if (catOf(prev.category_id)?.kind === 'labour') {
        if (p?.hourly_cost != null) next.unit_cost = String(p.hourly_cost);
        next.description = p ? `Labour — ${p.name}` : prev.description;
      }
      return next;
    });
  }

  const amount = form ? (Number(form.qty) || 0) * (Number(form.unit_cost) || 0) : 0;

  async function saveLine() {
    if (!form || busy) return;
    if (!form.description.trim()) { setError('Describe the cost'); return; }
    if (!(Number(form.qty) > 0)) { setError('Quantity must be more than 0'); return; }
    setBusy(true);
    setError('');
    const payload = {
      category_id: form.category_id || null, description: form.description, qty: Number(form.qty), unit: form.unit || null,
      unit_cost: Number(form.unit_cost) || 0, incurred_on: form.incurred_on, staff_id: form.staff_id || null,
      vendor: form.vendor, includes_gst: form.includes_gst, appointment_id: form.appointment_id || null,
      equipment_id: form.equipment_id || null, paid_by: form.paid_by || null
    };
    const r = form.id
      ? await costsService.updateLine(form.id, payload)
      : job
        ? await costsService.addJobLine(job, payload)
        : await costsService.addLine(appointment.id, payload);
    setBusy(false);
    if (!r.success) { setError(r.error || 'Could not save the cost'); return; }
    setForm(null);
    load(true);
  }
  async function removeLine(line) {
    const others = line.receipt ? line.receipt.line_count - 1 : 0;
    const receiptNote = line.receipt ? (others > 0 ? ' Its receipt stays with the other lines on it.' : ' Its receipt photo goes with it.') : '';
    if (!window.confirm(`Remove "${line.description}"?${receiptNote}`)) return;
    const r = await costsService.deleteLine(line.id);
    if (r.success) load(true);
    else setError(r.error || 'Could not remove the cost');
  }
  // Travel (GID-Session-014): home base → the job by road, one return trip per
  // booked day, fuel only. The server works out the km; every figure can be changed.
  async function openTravel() {
    const id = job || data?.jobId;
    if (!id) return;
    setError('');
    setForm(null);
    setTravel({ loading: true });
    const r = await costsService.travelForJob(id);
    if (!r.success) { setTravel(null); setError(r.error || 'Could not work out the travel'); return; }
    setTravel({
      loading: false, destination: r.destination, km_source: r.km_source, problem: r.problem,
      days: (r.visits || []).map((v) => ({ ...v, on: true })), // one trip per booked day; untick a day done on the way
      extra_trips: '0', trips: String(r.trips || 1), km_one_way: r.km_one_way === null ? '' : String(r.km_one_way),
      l_per_100km: String(r.l_per_100km || FUEL_DEFAULT_L_PER_100KM), fuel_price: r.fuel_price ? String(r.fuel_price) : '',
      return_trip: true, busy: false
    });
  }
  const setT = (k, v) => setTravel((t) => ({ ...t, [k]: v }));
  const toggleDay = (id) => setTravel((t) => ({ ...t, days: t.days.map((d) => (d.id === id ? { ...d, on: !d.on } : d)) }));
  // Trips = the ticked days plus any extra runs; a job with no booked days yet takes a plain number.
  const tripCount = (t) => (t.days?.length ? t.days.filter((d) => d.on).length + (Number(t.extra_trips) || 0) : Number(t.trips) || 0);
  const fuel = travel && !travel.loading ? fuelEstimate({ ...travel, trips: tripCount(travel) }) : null;
  async function addFuelLine() {
    if (!travel || travel.loading || travel.busy) return;
    if (!(tripCount(travel) > 0)) { setError('Tick at least one day, or add an extra trip'); return; }
    if (!(Number(travel.km_one_way) > 0)) { setError('Enter the distance each way in km'); return; }
    if (!(Number(travel.l_per_100km) > 0)) { setError('Enter the fuel use in litres per 100 km'); return; }
    if (!(Number(travel.fuel_price) > 0)) { setError('Enter the fuel price per litre (Settings › Job costs remembers it for next time)'); return; }
    const cat = cats.find((c) => c.kind === 'fuel') || cats.find((c) => c.kind === 'travel') || null;
    const t = tripCount(travel);
    const where = travel.destination?.suburb ? ` to ${travel.destination.suburb}` : '';
    const how = travel.km_source === 'road' ? ' by road' : travel.km_source === 'estimate' ? ' (estimated)' : '';
    const counted = travel.days.filter((d) => d.on).map((d) => fmtDate(d.date));
    const leftOut = travel.days.filter((d) => !d.on).map((d) => fmtDate(d.date));
    const extra = Number(travel.extra_trips) || 0;
    const daysNote = travel.days.length
      ? `; days: ${counted.join(', ') || 'none'}${extra ? ` + ${extra} extra trip${extra === 1 ? '' : 's'}` : ''}${leftOut.length ? ` (${leftOut.join(', ')} left out)` : ''}`
      : '';
    const payload = {
      category_id: cat ? cat.id : null,
      description: `Fuel — ${t} ${travel.return_trip ? 'return trip' : 'trip'}${t === 1 ? '' : 's'}${where} (${fuel.km_total} km @ ${travel.l_per_100km} L/100 km)`,
      qty: fuel.litres, unit: 'L', unit_cost: Number(travel.fuel_price), incurred_on: todayBrisbane(), includes_gst: true,
      notes: `${travel.km_one_way} km each way${how} × ${travel.return_trip ? 2 : 1} × ${t} = ${fuel.km_total} km; ${travel.l_per_100km} L/100 km → ${fuel.litres} L × $${travel.fuel_price}/L${daysNote}`,
      appointment_id: null
    };
    setError('');
    setTravel((x) => ({ ...x, busy: true }));
    const r = await costsService.addJobLine(job || data?.jobId, payload);
    if (!r.success) { setTravel((x) => ({ ...x, busy: false })); setError(r.error || 'Could not add the fuel cost'); return; }
    setTravel(null);
    load(true);
  }
  // Enter inside this panel saves the line instead of submitting the appointment.
  function onKeyDown(e) {
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
      e.preventDefault();
      e.stopPropagation();
      if (form) saveLine();
      else if (travel && !travel.loading) addFuelLine();
    }
  }

  const s = data?.summary;
  const revenueLabel = s?.revenue_source === 'quote' ? `Quoted · ${s.quote_number}`
    : s?.revenue_source === 'job_type_from' ? 'Job type · from'
      : s?.revenue_source === 'job_type' ? 'Job type price' : 'Quoted';

  return (
    <div className="form-group job-costs" onKeyDown={onKeyDown}>
      <div className="jc-head">
        <label>Costs{data?.jobNumber ? <span className="text-muted jc-head-note">on job {data.jobNumber}</span> : null} <span className="text-muted jc-head-note">internal · never shown to the client</span></label>
        {!form && !travel && (
          <span className="jc-head-actions">
            <button type="button" className="btn btn-sm btn-outline" onClick={openTravel} disabled={!data} title="Home base → this job by road, per booked day, fuel only">+ Travel</button>
            <button type="button" className="btn btn-sm btn-outline" onClick={startAdd} disabled={!data}>+ Add cost</button>
          </span>
        )}
      </div>

      {travel && (
        <div className="jc-travel">
          {travel.loading ? <p className="text-muted jc-hint">Working out the distance…</p> : (
            <>
              <p className="jc-travel-route">
                {travel.problem
                  ? <span className="form-error">{travel.problem} — or type the km in below.</span>
                  : <>Home base → <strong>{travel.destination?.address}</strong>: <strong>{travel.km_one_way} km</strong> each way{travel.km_source === 'road' ? ' by road' : ' (estimated — no road route found, straight line × 1.3)'}</>}
              </p>
              {travel.days.length > 0 && (
                <div className="jc-travel-days">
                  <span className="text-muted">One trip per booked day — untick a day done on the way:</span>
                  {travel.days.map((d) => (
                    <label key={d.id} className={`jc-day ${d.on ? 'on' : ''}`} title={d.on ? 'Counted' : 'Left out'}>
                      <input type="checkbox" checked={d.on} onChange={() => toggleDay(d.id)} /> {fmtDate(d.date)}{d.service_name ? ` · ${d.service_name}` : ''}
                    </label>
                  ))}
                  <label className="jc-travel-extra">+ extra trips<input aria-label="Extra trips" type="number" min="0" step="1" value={travel.extra_trips} onChange={(e) => setT('extra_trips', e.target.value)} /></label>
                </div>
              )}
              <div className="jc-form-row jc-travel-row">
                {travel.days.length === 0 && <label>Trips<input aria-label="Trips" type="number" min="1" step="1" value={travel.trips} onChange={(e) => setT('trips', e.target.value)} /></label>}
                <label>km each way<input aria-label="km each way" type="number" min="0" step="0.1" value={travel.km_one_way} onChange={(e) => setT('km_one_way', e.target.value)} /></label>
                <label>L / 100 km<input aria-label="Litres per 100 km" type="number" min="0" step="0.1" value={travel.l_per_100km} onChange={(e) => setT('l_per_100km', e.target.value)} /></label>
                <label>$ / litre<input aria-label="Fuel price per litre" type="number" min="0" step="0.01" value={travel.fuel_price} onChange={(e) => setT('fuel_price', e.target.value)} placeholder="Set in Settings" /></label>
                <label className="checkbox-label jc-travel-return"><input type="checkbox" checked={travel.return_trip} onChange={(e) => setT('return_trip', e.target.checked)} /> Return trips</label>
              </div>
              <div className="jc-form-row jc-form-foot">
                <span className="text-muted jc-travel-sum">{fuel.trips} {travel.return_trip ? 'return trip' : 'trip'}{fuel.trips === 1 ? '' : 's'} · {fuel.km_total} km · {fuel.litres} L · <strong>{formatMoney(fuel.cost)}</strong> fuel only{data?.defaults?.km_rate ? ` (the all-in ${formatMoney(data.defaults.km_rate)}/km rate is on + Add cost › Travel)` : ''}</span>
                <span className="spacer" />
                <button type="button" className="btn btn-sm btn-outline" onClick={() => setTravel(null)} disabled={travel.busy}>Cancel</button>
                <button type="button" className="btn btn-sm btn-primary" onClick={addFuelLine} disabled={travel.busy}>{travel.busy ? 'Adding…' : 'Add fuel cost'}</button>
              </div>
            </>
          )}
        </div>
      )}

      {s && !hideSummary && (
        <div className="jc-pl">
          <div className="jc-tile">
            <span className="jc-tile-label">{revenueLabel}</span>
            <strong>{s.revenue === null ? '—' : formatMoney(s.revenue)}</strong>
          </div>
          <div className="jc-tile">
            <span className="jc-tile-label">Costs</span>
            <strong>{formatMoney(s.total)}</strong>
            {s.line_count > 0 && <span className="jc-tile-sub">{s.line_count} line{s.line_count === 1 ? '' : 's'}</span>}
          </div>
          <div className="jc-tile" title="The job's share of the business's general costs (Business costs page)">
            <span className="jc-tile-label">Overheads</span>
            <strong>{s.overhead_share === null || s.overhead_share === undefined ? '—' : formatMoney(s.overhead_share)}</strong>
            {s.overhead?.working && <span className="jc-tile-sub">{s.overhead.working}</span>}
          </div>
          <div className={`jc-tile ${s.profit === null || s.profit === undefined ? '' : s.profit < 0 ? 'jc-neg' : 'jc-pos'}`}>
            <span className="jc-tile-label">Profit</span>
            <strong>{s.profit === null || s.profit === undefined ? '—' : money(s.profit)}</strong>
            {s.profit_pct !== null && s.profit_pct !== undefined && <span className="jc-tile-sub">{s.profit_pct}% of {s.revenue_source === 'quote' ? 'quote' : 'price'}{s.overhead_share ? ` · ${money(s.margin)} before overheads` : ''}</span>}
          </div>
          <div className="jc-tile">
            <span className="jc-tile-label">Cost / m²</span>
            <strong>{s.cost_per_m2 === null ? '—' : formatMoney(s.cost_per_m2)}</strong>
            {s.m2 > 0 && <span className="jc-tile-sub">{s.m2} m² measured{s.revenue_per_m2 !== null ? ` · quoted ${formatMoney(s.revenue_per_m2)}/m²` : ''}</span>}
          </div>
        </div>
      )}
      {s && s.revenue === null && (
        <p className="text-muted jc-hint">No quote booked and no fixed price on the job type, so there is no margin yet — costs still add up.</p>
      )}

      {data && data.lines.length === 0 && !form && <p className="text-muted jc-hint">No costs logged for this job yet.</p>}

      {data && data.lines.length > 0 && (
        <div className="jc-lines">
          {data.lines.map((l) => (form?.id === l.id ? (
            <LineForm key={l.id} form={form} setForm={setForm} cats={cats} people={people} visits={data.visits} equipment={data.defaults?.equipment || []} amount={amount} busy={busy}
              onSave={saveLine} onCancel={() => setForm(null)} onCategory={changeCategory} onPerson={changePerson} onEquipment={changeEquipment} />
          ) : (
            <div key={l.id} className="jc-line">
              <span className="jc-date">{fmtDate(l.incurred_on)}</span>
              <span className="jc-main">
                <span className="jc-desc">{l.description}</span>
                <span className="jc-meta text-muted">
                  {l.category_name || 'Uncategorised'}
                  {l.qty && l.unit_cost ? ` · ${fmtQty(l.qty)} ${l.unit || ''} × ${formatMoney(l.unit_cost)}` : ''}
                  {l.category_kind === 'labour' && l.staff_name ? ` · ${l.staff_name}` : ''}
                  {l.equipment_name ? ` · ${l.equipment_name}` : ''}
                  {l.vendor ? ` · ${l.vendor}` : ''}
                  {l.paid_by_name ? ` · paid by ${l.paid_by_name}` : ''}
                  {l.visit_starts_at && (data.visits?.length > 1 || !appointment) ? ` · visit ${formatApptDateLong(l.visit_starts_at)}` : ''}
                </span>
                {/* The shared receipt (backlog #687): the docket's pages, add / attach / take off */}
                <span className="jc-receipts">
                  <ReceiptThumbs kind="job_cost" rowId={l.id} receipt={l.receipt} onChange={() => load(true)} onLightbox={onLightbox} />
                </span>
              </span>
              <span className="jc-amount">{formatMoney(l.amount)}</span>
              <span className="jc-actions">
                <button type="button" className="jc-icon-btn" title="Edit" aria-label="Edit cost" onClick={() => edit(l)}>✎</button>
                <button type="button" className="jc-icon-btn jc-remove" title="Remove" aria-label="Remove cost" onClick={() => removeLine(l)}>×</button>
              </span>
            </div>
          )))}
        </div>
      )}

      {form && !form.id && (
        <LineForm form={form} setForm={setForm} cats={cats} people={people} visits={data?.visits || []} equipment={data?.defaults?.equipment || []} amount={amount} busy={busy}
          onSave={saveLine} onCancel={() => setForm(null)} onCategory={changeCategory} onPerson={changePerson} onEquipment={changeEquipment} />
      )}

      {s && s.by_category.length > 1 && (
        <div className="jc-cats text-muted">
          {s.by_category.map((c) => <span key={c.category_id || 'none'}>{c.name} <strong>{formatMoney(c.amount)}</strong></span>)}
        </div>
      )}
      {error && <div className="form-error jc-error">{error}</div>}
    </div>
  );
}

function LineForm({ form, setForm, cats, people, visits = [], equipment = [], amount, busy, onSave, onCancel, onCategory, onPerson, onEquipment }) {
  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  const cat = cats.find((c) => String(c.id) === String(form.category_id));
  const isLabour = cat?.kind === 'labour';
  const isEquipment = cat?.kind === 'equipment';
  const liveVisits = visits.filter((v) => !['cancelled', 'no_show'].includes(v.status));
  return (
    <div className="jc-form">
      <div className="jc-form-row">
        <select aria-label="Category" value={form.category_id} onChange={(e) => onCategory(e.target.value)}>
          <option value="">Category…</option>
          {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <input aria-label="Description" className="jc-desc-input" placeholder="What was it?" value={form.description} onChange={(e) => set('description', e.target.value)} />
      </div>
      <div className="jc-form-row jc-form-nums">
        <input aria-label="Quantity" type="number" min="0" step="0.01" value={form.qty} onChange={(e) => set('qty', e.target.value)} placeholder="Qty" />
        <select aria-label="Unit" value={form.unit} onChange={(e) => set('unit', e.target.value)}>
          {COST_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
        </select>
        <span className="jc-x">×</span>
        <div className="jc-money">
          <span>$</span>
          <input aria-label="Unit cost" type="number" min="0" step="0.01" value={form.unit_cost} onChange={(e) => set('unit_cost', e.target.value)} placeholder="0.00" />
        </div>
        <span className="jc-eq">= <strong>{formatMoney(amount)}</strong></span>
      </div>
      <div className="jc-form-row">
        <input aria-label="Date" type="date" value={form.incurred_on} onChange={(e) => set('incurred_on', e.target.value)} />
        {isLabour && (
          <select aria-label="Whose hours" value={form.staff_id} onChange={(e) => onPerson(e.target.value)}>
            <option value="">Whose hours?</option>
            {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        <input aria-label="Supplier" placeholder="Supplier (optional)" value={form.vendor} onChange={(e) => set('vendor', e.target.value)} />
        {!isLabour && !isEquipment && (
          <select aria-label="Paid by" value={form.paid_by || ''} onChange={(e) => set('paid_by', e.target.value)} title="Who fronted the cash for this — feeds the Between us ledger on Business costs">
            <option value="">Paid by… (optional)</option>
            {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        {isEquipment && equipment.length > 0 && (
          <select aria-label="Which equipment" value={form.equipment_id || ''} onChange={(e) => onEquipment(e.target.value)} title="From the register on Business costs — its hourly rate fills in the unit cost">
            <option value="">Hired / not on the register</option>
            {equipment.map((e) => <option key={e.id} value={e.id}>{e.name} · {formatMoney(e.hourly_rate)}/h</option>)}
          </select>
        )}
        {liveVisits.length > 1 && (
          <select aria-label="Which visit" value={form.appointment_id || ''} onChange={(e) => set('appointment_id', e.target.value)} title="The day this cost belongs to">
            <option value="">Whole job</option>
            {liveVisits.map((v, i) => <option key={v.id} value={v.id}>Visit {i + 1} · {formatApptDateLong(v.starts_at)}</option>)}
          </select>
        )}
      </div>
      <div className="jc-form-row jc-form-foot">
        <label className="checkbox-label jc-gst">
          <input type="checkbox" checked={!!form.includes_gst} onChange={(e) => set('includes_gst', e.target.checked)} /> Amount includes GST
        </label>
        <span className="spacer" />
        <button type="button" className="btn btn-sm btn-outline" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" className="btn btn-sm btn-primary" onClick={onSave} disabled={busy}>{busy ? 'Saving…' : (form.id ? 'Save' : 'Add cost')}</button>
      </div>
    </div>
  );
}
